/**
 * Клиент для запросов к серверу (Supabase Edge Functions)
 *
 * Authorization: Bearer <anonKey> — для Supabase gateway (всегда anonKey)
 * X-User-Token: <userJWT> — user token для идентификации на сервере (gateway НЕ валидирует)
 */

import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from './supabase';

// Edge Function endpoint — префикс make-server-eaddf3b5 зафиксирован
// в supabase/functions/make-server-eaddf3b5/index.tsx (роуты Hono).
const SERVER_BASE = `${SUPABASE_URL}/functions/v1/make-server-eaddf3b5`;

/** Порог для принудительного рефреша токена (60 сек до истечения) */
const TOKEN_REFRESH_THRESHOLD_SEC = 60;

/**
 * Получить актуальный access_token текущего пользователя
 * Рефрешит сессию если токен скоро истечёт
 */
async function getUserAccessToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return null;

  // Проверяем, не истёк ли токен (с запасом)
  const expiresAt = session.expires_at ?? 0;
  const now = Math.floor(Date.now() / 1000);

  if (expiresAt - now < TOKEN_REFRESH_THRESHOLD_SEC) {
    // Токен истёк или скоро истечёт — рефрешим
    const { data: { session: refreshed }, error } = await supabase.auth.refreshSession();
    if (error || !refreshed) {
      console.error(`Не удалось обновить сессию: ${error?.message ?? 'сессия пуста'}`);
      return null;
    }
    return refreshed.access_token;
  }

  return session.access_token;
}

/**
 * Сформировать заголовки для запроса к серверу
 * Authorization — всегда anonKey (для Supabase gateway)
 * X-User-Token — user JWT для идентификации на сервере
 */
async function buildHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    'Content-Type': 'application/json',
  };

  const userToken = await getUserAccessToken();
  if (userToken) {
    headers['X-User-Token'] = userToken;
  }

  return headers;
}

/**
 * GET-запрос к серверу
 */
export async function serverGet<T>(path: string): Promise<T> {
  const headers = await buildHeaders();
  const response = await fetch(`${SERVER_BASE}${path}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Ошибка сервера GET ${path}: ${response.status} — ${errorBody}`);
  }

  return response.json() as Promise<T>;
}

/**
 * POST-запрос к серверу
 */
export async function serverPost<T>(path: string, body: unknown): Promise<T> {
  const headers = await buildHeaders();
  const response = await fetch(`${SERVER_BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Ошибка сервера POST ${path}: ${response.status} — ${errorBody}`);
  }

  return response.json() as Promise<T>;
}

/**
 * PUT-запрос к серверу
 */
export async function serverPut<T>(path: string, body: unknown): Promise<T> {
  const headers = await buildHeaders();
  const response = await fetch(`${SERVER_BASE}${path}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Ошибка сервера PUT ${path}: ${response.status} — ${errorBody}`);
  }

  return response.json() as Promise<T>;
}

/**
 * DELETE-запрос к серверу
 */
export async function serverDelete<T>(path: string): Promise<T> {
  const headers = await buildHeaders();
  const response = await fetch(`${SERVER_BASE}${path}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Ошибка сервера DELETE ${path}: ${response.status} — ${errorBody}`);
  }

  return response.json() as Promise<T>;
}